import React from 'react';
import { motion } from 'framer-motion';
import { dictionary } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';

export interface FunnelEventCount {
  event_type: string;
  count: number;
}

interface FunnelChartProps {
  events: FunnelEventCount[];
}

const STAGE_ORDER = [
  'pageview',
  'addToCart',
  'initiateCheckout',
  'addCheckoutInfo',
  'addPaymentInfo',
  'purchase',
];

const STAGE_LABEL_KEY: Record<string, string> = {
  pageview: 'funnel-stage-pageview',
  addToCart: 'funnel-stage-addToCart',
  initiateCheckout: 'funnel-stage-initiateCheckout',
  addCheckoutInfo: 'funnel-stage-addCheckoutInfo',
  addPaymentInfo: 'funnel-stage-addPaymentInfo',
  purchase: 'funnel-stage-purchase',
};

const FLUXI_PURPLE = '#6B48FF';

const FunnelChart: React.FC<FunnelChartProps> = ({ events }) => {
  const { language } = useAuth();

  const countMap: Record<string, number> = {};
  events.forEach((e) => {
    countMap[e.event_type] = e.count;
  });

  const stages = STAGE_ORDER.map((key) => ({
    key,
    label: dictionary[STAGE_LABEL_KEY[key]]?.[language] ?? key,
    count: countMap[key] ?? 0,
  }));

  const maxCount = Math.max(...stages.map((s) => s.count), 1);

  // Square-root scaling: compresses the top, expands the bottom
  // so even the last steps show visible width differences
  const getWidthPct = (count: number) => {
    if (count === 0) return 15;
    return Math.max(Math.sqrt(count / maxCount) * 100, 15);
  };

  if (stages.every((s) => s.count === 0)) {
    return (
      <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          {dictionary['conversion-funnel']?.[language]}
        </h2>
        <p className="text-center text-gray-400 py-8">
          {dictionary['no-funnel-data']?.[language]}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">
        {dictionary['conversion-funnel']?.[language]}
      </h2>

      <div className="flex flex-col items-center gap-1">
        {stages.map((stage, i) => {
          const widthPct = getWidthPct(stage.count);
          const isEmpty = stage.count === 0;
          const prevCount = i > 0 ? stages[i - 1].count : null;
          const dropOffPct =
            prevCount && prevCount > 0
              ? (((prevCount - stage.count) / prevCount) * 100).toFixed(1)
              : null;

          return (
            <React.Fragment key={stage.key}>
              {dropOffPct !== null && (
                <div className="w-full flex justify-center py-1">
                  <span className="text-xs text-gray-400">
                    ↓ {dropOffPct}% {dictionary['funnel-drop-off']?.[language]}
                  </span>
                </div>
              )}

              <div className="w-full flex items-center gap-3">
                {/* Left: conversion from top of funnel */}
                <div className="w-20 text-right shrink-0">
                  <span className="text-xs font-medium text-gray-400">
                    {stages[0].count > 0
                      ? ((stage.count / stages[0].count) * 100).toFixed(1)
                      : '0.0'}%
                  </span>
                </div>

                {/* Center: bar */}
                <div className="flex-1 flex justify-center">
                  <motion.div
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={{ scaleX: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: i * 0.1, ease: 'easeOut' }}
                    style={{
                      width: `${widthPct}%`,
                      transformOrigin: 'center',
                      backgroundColor: isEmpty ? '#F3F4F6' : `${FLUXI_PURPLE}1A`,
                      borderRadius: '10px',
                      borderStyle: isEmpty ? 'dashed' : 'none',
                      borderWidth: isEmpty ? '1.5px' : '0',
                      borderColor: isEmpty ? '#D1D5DB' : 'transparent',
                    }}
                    className="px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="text-sm font-semibold truncate"
                        style={{ color: isEmpty ? '#9CA3AF' : FLUXI_PURPLE }}
                      >
                        {stage.label}
                      </span>
                      <span
                        className="text-sm font-bold shrink-0"
                        style={{ color: isEmpty ? '#9CA3AF' : FLUXI_PURPLE }}
                      >
                        {stage.count.toLocaleString()}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Right: absolute count */}
                <div className="w-20 shrink-0">
                  <span className="text-xs font-medium text-gray-400">
                    {stage.count.toLocaleString()}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default FunnelChart;
